'use client';

import { useState, useEffect } from 'react';
import { Mail, X, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useI18n } from './i18n-provider';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function FloatingEmail() {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const { t, isRtl } = useI18n();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: formData.get('companyName') || 'Quick Inquiry',
          city: formData.get('city') || '',
          contactPerson: formData.get('contactPerson'),
          email: formData.get('email'),
          whatsapp: formData.get('whatsapp') || '',
          estimatedVolume: '',
          interestedIn: formData.get('interestedIn') || 'Quick Inquiry',
          requirements: formData.get('requirements'),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setStatus('success');
      form.reset();
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  return (
    <div
      className={`fixed bottom-6 right-6 z-[60] transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Expanded Panel */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-[360px] sm:w-[400px] rounded-2xl bg-[oklch(0.20_0.015_220)] border border-[oklch(0.28_0.025_170)] shadow-2xl shadow-black/40 shadow-[0_0_40px_oklch(0.62_0.19_150)_/_0.15] overflow-hidden animate-[fadeInUp_0.3s_ease-out]">
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-[oklch(0.62_0.19_150)]/20 to-[oklch(0.85_0.12_140)]/10 border-b border-[oklch(0.28_0.025_170)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[oklch(0.62_0.19_150)]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[oklch(0.72_0.22_150)]" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm">{t.floatingEmail.title}</h3>
                <p className="text-xs text-muted-foreground">{t.floatingEmail.subtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg hover:bg-[oklch(0.26_0.02_220)] text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-[oklch(0.72_0.22_150)]" />
                </div>
                <h3 className="text-lg font-bold mb-2">{t.floatingEmail.successTitle}</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {t.floatingEmail.successDesc}
                </p>
                <Button
                  onClick={() => {
                    setStatus('idle');
                    setIsOpen(false);
                  }}
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                >
                  {t.floatingEmail.close}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="float-name" className="text-xs">
                      {t.floatingEmail.name} <span className="text-[oklch(0.577_0.245_27.325)]">*</span>
                    </Label>
                    <Input
                      id="float-name"
                      name="contactPerson"
                      required
                      placeholder="Your name"
                      className="h-9 rounded-lg text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="float-email" className="text-xs">
                      {t.floatingEmail.email} <span className="text-[oklch(0.577_0.245_27.325)]">*</span>
                    </Label>
                    <Input
                      id="float-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@company.com"
                      className="h-9 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="float-company" className="text-xs">
                    {t.floatingEmail.company}
                  </Label>
                  <Input
                    id="float-company"
                    name="companyName"
                    placeholder="Your company name"
                    className="h-9 rounded-lg text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="float-city" className="text-xs">
                    {t.floatingEmail.city}
                  </Label>
                  <Input
                    id="float-city"
                    name="city"
                    placeholder="e.g. Dubai, UAE"
                    className="h-9 rounded-lg text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="float-interested" className="text-xs">
                    {t.floatingEmail.interested}
                  </Label>
                  <select
                    id="float-interested"
                    name="interestedIn"
                    className="w-full h-9 rounded-lg text-sm bg-[oklch(0.25_0.02_170)] border border-[oklch(0.28_0.025_170)] text-foreground px-3 focus:outline-none focus:ring-2 focus:ring-[oklch(0.62_0.19_150)]/50 focus:border-[oklch(0.62_0.19_150)]"
                  >
                    <option value="Quick Inquiry">Quick Inquiry</option>
                    <option value="NEV Spare Parts Wholesale">NEV Spare Parts Wholesale</option>
                    <option value="Technical Training & Escort">Technical Training & Escort</option>
                    <option value="EV Battery Repair Program">EV Battery Repair Program</option>
                    <option value="Distributor / Agent Partnership">Distributor / Agent Partnership</option>
                    <option value="Garage Transformation">Garage Transformation</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="float-message" className="text-xs">
                    {t.floatingEmail.message} <span className="text-[oklch(0.577_0.245_27.325)]">*</span>
                  </Label>
                  <Textarea
                    id="float-message"
                    name="requirements"
                    required
                    placeholder="Tell us about your needs..."
                    rows={3}
                    className="rounded-lg text-sm resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-400">{errorMessage}</p>
                )}

                <Button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full btn-glow h-10 rounded-xl text-sm"
                >
                  {status === 'submitting' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      {t.common.sending}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t.floatingEmail.send}
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  {t.floatingEmail.orEmail}{' '}
                  <a
                    href="mailto:wfdnewenergy@163.com"
                    className="text-[oklch(0.72_0.22_150)] hover:underline"
                  >
                    wfdnewenergy@163.com
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[oklch(0.62_0.19_150)] to-[oklch(0.72_0.22_150)] shadow-[0_4px_24px_oklch(0.62_0.19_150)_/_0.4] hover:shadow-[0_6px_32px_oklch(0.62_0.19_150)_/_0.6] hover:scale-110 transition-all duration-300 flex items-center justify-center group"
        aria-label="Email us"
      >
        <Mail
          className={`w-6 h-6 text-[oklch(0.12_0.02_150)] transition-transform duration-300 ${
            isOpen ? 'scale-0 rotate-90' : 'scale-100'
          }`}
        />
        <X
          className={`absolute w-6 h-6 text-[oklch(0.12_0.02_150)] transition-transform duration-300 ${
            isOpen ? 'scale-100' : 'scale-0 -rotate-90'
          }`}
        />
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[oklch(0.72_0.22_150)]/30 animate-ping" />
      </button>
    </div>
  );
}
